import { useContext } from 'react';
import { Stack, Typography, Box, Chip, Avatar, Divider } from '@mui/material';
import CakeIcon from '@mui/icons-material/Cake';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import DashboardWidgetCard from '../DashboardWidgetCard';
import useWidgetData from '../useWidgetData';
import AuthContext from '../../../context/authContext';

// Two-letter initials from a person's display name (fallback for the avatar).
const initials = name =>
  String(name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('');

// A single colleague row: avatar, name, birthday date (+ age) and a status chip.
function PersonRow({ entry, accent, isToday }) {
  return (
    <Stack direction='row' alignItems='center' spacing={1}>
      <Avatar
        sx={theme => ({
          width: 28,
          height: 28,
          fontSize: '0.72rem',
          bgcolor: isToday ? `${accent}.main` : theme.palette.action.selected,
          color: isToday ? theme.palette[accent]?.contrastText : theme.palette.text.secondary,
        })}
      >
        {initials(entry.name) || <CakeIcon sx={{ fontSize: 16 }} />}
      </Avatar>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant='body2' sx={{ fontWeight: 600 }} noWrap title={entry.name}>
          {entry.name}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {dayjs(entry.birthday).locale('de').format('dd, DD.MM.')}
          {entry.age ? ` · wird ${entry.age}` : ''}
        </Typography>
      </Box>
      {isToday ? (
        <Chip size='small' color={accent} label='Heute 🎉' />
      ) : (
        <Chip
          size='small'
          variant='outlined'
          label={entry.inDays === 1 ? 'morgen' : `in ${entry.inDays} T`}
        />
      )}
    </Stack>
  );
}

// Shows birthdays of colleagues at the user's own location, split into
// "Hat heute" (today) and "Hat bald" (coming up). The branch scope is decided
// by the backend; the requesting user is filtered out client-side.
export default function BirthdaysWidget({ widget, editMode, onHide, dragHandleProps }) {
  const { payload, loading, error, refresh } = useWidgetData(widget.id);
  const authCtx = useContext(AuthContext);
  const currentUserId = authCtx?.currentUser?.userId || null;
  const accent = widget.accent || 'secondary';

  const withoutSelf = list =>
    (Array.isArray(list) ? list : []).filter(entry => entry.userId !== currentUserId);

  const today = withoutSelf(payload?.today);
  const upcoming = withoutSelf(payload?.upcoming);
  const isEmpty = !loading && !error && today.length === 0 && upcoming.length === 0;

  return (
    <DashboardWidgetCard
      title={widget.title}
      icon={widget.icon}
      accent={accent}
      loading={loading}
      error={error}
      onRetry={() => refresh({ forceRecompute: false })}
      empty={isEmpty}
      emptyText='Keine anstehenden Geburtstage.'
      editMode={editMode}
      onHide={onHide}
      dragHandleProps={dragHandleProps}
      footer={
        payload?.scope === 'noBranch' ? (
          <Typography variant='caption' color='text.secondary'>
            Kolleg*innen ohne Standortzuordnung
          </Typography>
        ) : null
      }
    >
      <Stack spacing={1.25} sx={{ flexGrow: 1 }}>
        {today.length > 0 && (
          <Box>
            <Typography variant='caption' sx={{ fontWeight: 700, color: `${accent}.main` }}>
              Hat heute
            </Typography>
            <Stack spacing={0.75} sx={{ mt: 0.5 }}>
              {today.map(entry => (
                <PersonRow key={entry.userId} entry={entry} accent={accent} isToday />
              ))}
            </Stack>
          </Box>
        )}

        {upcoming.length > 0 && (
          <Box>
            {today.length > 0 && <Divider sx={{ mb: 1 }} />}
            <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.secondary' }}>
              Hat bald
            </Typography>
            <Stack spacing={0.75} sx={{ mt: 0.5 }}>
              {upcoming.map(entry => (
                <PersonRow key={entry.userId} entry={entry} accent={accent} />
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </DashboardWidgetCard>
  );
}
